import React, { useContext, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import "../../styles/filesComponent.pc.css";
import "./HistoryProject.css";
import "../../../globalStyles/dataTableStyles.css";
import OpenBlock from "../../../assets/openBlock.svg";
import CloseBlockWindow from "../../../assets/arrowCloseWindow.svg";
import { Context } from "../../../index";
import { IFunctionPermissions } from "../../../common/api_interfaces/role/IFunctionPermissions";
import { useParams } from "react-router-dom";
import HistoryTable from "../../Shared/HistoryTable/HistoryTable";
import Search from "../../UiLib/Search/Search";
import { ProjectHistoryEntities } from "../../../models/HistoryModels/HistoryEntities";
import { ProjectHistoryActions } from "../../../models/HistoryModels/HistoryActions";
import FilterContainer from "../../Shared/Filter/FilterContainer";
import { FilterOptionsExtented, SelectedOptions } from "../../../models/FilterModels/IFilterModel";
import { hasPermission } from "../../../helpers/hasPermisson";
import axios from "axios";
import { AppContext } from "../../../App";
import { IProjectHistoryFilter } from "../../../common/api_interfaces/history/IProjectHistoryFilter";
import { IProjectHistoryDisplay } from "../../../common/api_interfaces/history/IProjectHistoryDisplay";
import { EntityType, TypeOfChange } from "../../../common/enums/TypeOfChange";

export const HISTORY_PAGE_SIZE = 10;
const FULL_SCREEN_PAGE_SIZE = 30;

interface IHistoryProjectProps {
    records: IProjectHistoryDisplay[];
    loadPage: (skip: number, take: number, filters: IProjectHistoryFilter, append: boolean) => Promise<boolean>;
    userAccess: IFunctionPermissions[];
}

const History: React.FC<IHistoryProjectProps> = ({ records, loadPage, userAccess }) => {
    const { id } = useParams();
    const { showToast } = useContext(AppContext);
    const [skip, setSkip] = useState<number>(0);
    const [haveMoreRecords, setHaveMoreRecords] = useState<boolean>(true);
    const [openOnFullWindow, setOpenOnFullWindow] = useState(false);
    const { store } = useContext(Context);
    const isCollapsed = store.leftMenuCollapsed;
    //Фильтры
    const [searchText, setSearchText] = useState<string>("");
    const [filterCounter, setFilterCounter] = useState<number>(0);
    const [historyFilters, setHistoryFilters] = useState<FilterOptionsExtented>();
    const [selectedFilters, setSelectedFilters] = useState<SelectedOptions>({});

    // Ограничение прав
    const [viewHistory, setViewHistory] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const widget = containerRef.current;

        if (openOnFullWindow && widget) {
            widget?.scrollIntoView({ behavior: "auto", block: "center" });
        }
    }, [openOnFullWindow]);

    useEffect(() => {
        (async () => {
            try {
                const response = await store.filterService.getFilters({
                    projectId: Number(id),
                    projectUser: true,
                });
                const data = response.data;

                const options: FilterOptionsExtented = {
                    historyAuthor: data.projectUsers,
                    entityType: Array.from(ProjectHistoryEntities).map((entity) => ({
                        name: entity[1],
                        code: entity[0],
                    })),
                    actionType: Array.from(ProjectHistoryActions).map((action) => ({
                        name: action[1],
                        code: action[0],
                    })),
                };

                setHistoryFilters(options);
            } catch (error: any) {
                if (axios.isAxiosError(error)) {
                     showToast(error.response?.data?.message, error.response?.data?.details);
                }
            }
        })();
    }, []);

    useEffect(() => {
        setViewHistory(hasPermission(userAccess, "ProjectHistory", "view"));
    }, [userAccess]);

    useEffect(() => {
        (async () => {
            if (openOnFullWindow && records.length < FULL_SCREEN_PAGE_SIZE) {
                await loadRecords(0, false);
            }
        })();
    }, [openOnFullWindow]);

    useEffect(() => {
        setOpenOnFullWindow(false);
    }, [id]);

    useEffect(() => {
        (async () => {
            if (searchText.length === 0 && Object.keys(selectedFilters).length === 0) await loadRecords(0, false);
        })();
    }, [searchText, selectedFilters]);

    const handleScrolledToBottom = async () => {
        if (haveMoreRecords) {
            await loadRecords(skip, true);
        }
    };

    const onCancelClick = async () => {
        setSelectedFilters({});
        setSearchText("");
    };

    async function onSearchSubmit() {
        await loadRecords(0, false);
    }

    const applyFilters = async () => {
        await loadRecords(0, false);
    };

    const loadRecords = async (from: number, append: boolean) => {
        const pageSize = openOnFullWindow ? FULL_SCREEN_PAGE_SIZE : HISTORY_PAGE_SIZE;
        const filters: IProjectHistoryFilter = {
            projectId: Number(id),
            dateFrom: selectedFilters.dates?.[0].toISOString(),
            dateTo: selectedFilters.dates?.[1]?.toISOString(),
            userIds: selectedFilters.historyAuthorIds,
            entityTypes: selectedFilters.entityTypes as EntityType[],
            actionTypes: selectedFilters.actionTypes as TypeOfChange[],
            entityName: searchText,
        };
        setHaveMoreRecords(await loadPage(from, pageSize, filters, append));
        setSkip(from + pageSize);
    };

    return (
        <div
            id="main-history-widget"
            className={`project-history-widget ${openOnFullWindow ? "widget_wrapper--full__window" : "widget_wrapper"}`}
        >
            <div
                className={openOnFullWindow ? "widget_wrapper-full__window--block" : ""}
                style={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
                <div className={"widgets_header--container"}>
                    {openOnFullWindow ? (
                        <div onClick={() => setOpenOnFullWindow(false)} className="widgets_header--open__block">
                            <img src={CloseBlockWindow} />
                        </div>
                    ) : (
                        <div
                            onClick={() => setOpenOnFullWindow(true)}
                            className="widgets_header--open__block"
                            ref={containerRef}
                        >
                            <img src={OpenBlock} />
                        </div>
                    )}
                </div>
                <div className="widjet_content-block">
                    <div className="widgets_header">
                        <div className="widjets_header_left">
                            <h2 className="widget-card-header-style">История проекта</h2>
                        </div>
                        <div className="widjets_header_rigth">
                            <Search
                                value={searchText}
                                onSubmitFunc={onSearchSubmit}
                                changeSearchValue={setSearchText}
                            />
                            <FilterContainer
                                filterCounter={filterCounter}
                                setFilterCounter={setFilterCounter}
                                selectedOptions={selectedFilters}
                                setSelectedOptions={setSelectedFilters}
                                filterOptions={historyFilters}
                                showResetButton={searchText.length > 0}
                                onReset={onCancelClick}
                                showApplyButton
                                onApply={applyFilters}
                            />
                        </div>
                    </div>
                    <div className="widjet-table-container">
                        {viewHistory ? (
                            <HistoryTable
                                showFullChanges={openOnFullWindow}
                                records={records}
                                onScrolledToBottom={handleScrolledToBottom}
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default observer(History);
